import { useMemo } from 'react';
import { pickSpaceColor } from '../theme/tokens.ts';
import { getSpaceIconDef, resolveSpaceIconKey, resolveSpaceColor } from './spaceIcons.ts';

/**
 * Аватар пространства: цветная solid-плитка + содержимое.
 * Содержимое по приоритету: иконка из курируемого реестра (`space.icon`
 * вида `'i:<key>'`) → эмодзи (любая другая непустая строка) → 1–2 буквы из
 * названия. Цвет плитки — явный `color` или детерминированно из seed
 * (id/название), чтобы быть стабильным между сессиями. Заменяет прежний
 * фолбэк «◆» и унифицирует силуэт в рельсе/на home/в палитре.
 */

interface SpaceAvatarProps {
  name: string;
  icon?: string | null;
  /** Стабильный seed для выбора цвета (обычно space.id). По умолчанию — name. */
  seed?: string;
  /** Явный цвет плитки (если задан в пространстве). */
  color?: string | null;
  size?: number;
  className?: string;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Полупрозрачный оттенок hex-цвета (#RRGGBB + альфа), иначе исходный цвет. */
function withAlpha(hex: string, alpha: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : hex;
}

export function SpaceAvatar({
  name,
  icon,
  seed,
  color,
  size = 28,
  className,
}: SpaceAvatarProps): JSX.Element {
  // Приоритет цвета: явный проп → выбранный в `icon` (@RRGGBB) → детерминированный.
  const bg = useMemo(
    () => color ?? resolveSpaceColor(icon) ?? pickSpaceColor(seed ?? name),
    [color, icon, seed, name],
  );
  const iconKey = resolveSpaceIconKey(icon);
  const iconDef = iconKey ? getSpaceIconDef(iconKey) : undefined;
  // Серьёзный тон: эмодзи как системные глифы не рендерим — либо иконка из
  // курируемого набора ('i:<key>'), либо нейтральная буквенная монограмма.

  return (
    <span
      className={className}
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        boxSizing: 'border-box',
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.32),
        background: withAlpha(bg, '1f'),
        border: `1px solid ${withAlpha(bg, '38')}`,
        color: bg,
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
        letterSpacing: '0.01em',
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {iconDef ? (
        <iconDef.Icon size={Math.round(size * 0.52)} color={bg} strokeWidth={1.9} />
      ) : (
        initials(name)
      )}
    </span>
  );
}
